import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    CheckCircle2, TrendingUp, ShieldCheck, BarChart3, Package,
    ShoppingBag, Coffee, Truck, Settings, HeartPulse, MonitorCheck,
    PlayCircle, Star, Zap, Users, Cloud, LayoutDashboard, FileText,
    Smartphone, Printer, FileSpreadsheet, PlusCircle, CreditCard, ChevronDown, Check
} from 'lucide-react';
import SEO from '../../components/SEO';
import { usePublic } from '../../context/PublicContext';

const HomePage = () => {
    const { openDemoModal } = usePublic();
    const [activeFaq, setActiveFaq] = useState(null);
    const [activeBillingType, setActiveBillingType] = useState(0);

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const stats = [
        { label: 'Businesses', value: '500+', icon: Users },
        { label: 'Invoices Generated', value: '10,000+', icon: FileText },
        { label: 'Secure Cloud Backup', value: '99.9%', icon: Cloud },
        { label: 'Multi-Device Access', value: 'Seamless', icon: Smartphone }
    ];

    const billingTypes = [
        { name: 'Walk-in Bill', desc: 'Quick POS billing for regular customers.' },
        { name: 'Customer Bill', desc: 'Detailed invoice assigned to a saved customer.' },
        { name: 'Credit / Pending Bill', desc: 'Manage unpaid invoices and credit ledgers.' },
        { name: 'GST Bill', desc: 'Fully compliant tax invoice with CGST/SGST.' },
        { name: 'Non-GST Bill', desc: 'Simple cash memo without tax components.' }
    ];

    const features = [
        { title: 'GST Billing', desc: 'Auto CGST, SGST, IGST calculations.', icon: FileSpreadsheet },
        { title: 'Inventory Management', desc: 'Track stock, batches, and expiries.', icon: Package },
        { title: 'Barcode Billing', desc: 'Scan and add products instantly.', icon: Zap },
        { title: 'WhatsApp Sharing', desc: 'Send invoices directly to customers.', icon: Smartphone },
        { title: 'Customer Management', desc: 'Track ledgers and credit history.', icon: Users },
        { title: 'Sales Tracker', desc: 'Monitor your sales performance easily.', icon: TrendingUp },
        { title: 'Reports & Analytics', desc: 'Detailed sales and tax reports.', icon: BarChart3 },
        { title: 'Thermal Printer', desc: 'Print 2-inch and 3-inch receipts.', icon: Printer },
    ];

    const industries = [
        { title: 'Retail Shops', icon: ShoppingBag },
        { title: 'Supermarkets', icon: ShoppingBag },
        { title: 'Medical Stores', icon: HeartPulse },
        { title: 'Restaurants', icon: Coffee },
        { title: 'Jewelry Shops', icon: CheckCircle2 },
        { title: 'Salons', icon: Users },
        { title: 'Electronics Stores', icon: MonitorCheck },
        { title: 'Wholesale Businesses', icon: Truck },
    ];

    const faqs = [
        { q: 'Is this GST compliant?', a: 'Yes, Gio Billing is fully GST compliant, supporting HSN/SAC codes, tax slabs, and GSTR report generation.' },
        { q: 'Does it support thermal printers?', a: 'Absolutely. It supports both standard A4/A5 printers and 2/3-inch thermal receipt printers.' },
        { q: 'Can I use it on mobile?', a: 'Yes, our platform is fully responsive and we have a dedicated mobile app for billing on the go.' },
        { q: 'Is cloud backup available?', a: 'Yes, your data is securely backed up to the cloud in real-time, ensuring zero data loss.' },
        { q: 'Does it support barcode billing?', a: 'Yes, you can easily connect a barcode scanner to add items to the cart instantly.' },
        { q: 'Does it support multiple billing types?', a: 'Yes, you can easily switch between Walk-in, Customer, Credit, GST, and Non-GST bills.' }
    ];

    return (
        <div className="overflow-hidden bg-[#fafafa] font-outfit text-gray-900 selection:bg-green-100 selection:text-green-900">
            <SEO
                title="Smarter Billing & Inventory Software"
                description="Manage invoices, inventory, customers, sales, GST, and reports — all in one powerful cloud platform."
                url="/"
            />

            {/* 1. Hero Section */}
            <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden bg-white border-b border-gray-200/50">
                {/* Modern Grid Pattern Background */}
                <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>
                
                {/* Gradient Blurs */}
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-green-500/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3"></div>
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
                    <motion.div
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <span className="inline-block py-1.5 px-4 rounded-full bg-green-50 border border-green-100 text-green-600 font-bold text-sm mb-6 shadow-sm">
                            ✨ The Future of Business Management
                        </span>
                        
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight">
                            Fast, Smart Billing & <br className="hidden sm:block" />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">Inventory Business Management</span>
                        </h1>
                        
                        <p className="text-xl text-gray-600 leading-relaxed font-medium mb-10 max-w-2xl mx-auto">
                            Manage invoices, inventory, customers, sales, GST, and reports — all in one powerful cloud platform designed for speed.
                        </p>

                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-16">
                            <button
                                onClick={openDemoModal}
                                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gray-900 text-white font-bold text-lg hover:bg-black shadow-xl shadow-gray-900/20 transition-all hover:-translate-y-0.5"
                            >
                                Start Free Trial
                            </button>
                            <button
                                onClick={openDemoModal}
                                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white text-gray-900 border border-gray-200 font-bold text-lg hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all flex items-center justify-center gap-2"
                            >
                                Book Demo <PlayCircle size={20} className="text-green-500" />
                            </button>
                        </div>
                    </motion.div>

                    {/* Dashboard Preview Mockup */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="relative max-w-5xl mx-auto perspective-2000"
                    >
                        <div className="bg-white/40 backdrop-blur-xl p-2 rounded-[2rem] border border-white/60 shadow-2xl relative z-10">
                            <div className="bg-gray-900 rounded-[1.5rem] overflow-hidden border border-gray-800 shadow-inner relative aspect-[16/9]">
                                {/* Header bar mockup */}
                                <div className="h-10 border-b border-gray-800 flex items-center px-4 gap-2 bg-gray-900/80 backdrop-blur-md">
                                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                                </div>
                                <img src="/gio_billing_dashboard_mockup.png" alt="Dashboard Preview" className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity" />
                            </div>
                        </div>

                        {/* Floating Elements */}
                        <div className="absolute -left-12 top-1/4 w-64 bg-white p-4 rounded-2xl shadow-2xl border border-gray-100 z-20 hidden lg:block transform -rotate-6 hover:rotate-0 transition-transform duration-300">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600"><Check size={20}/></div>
                                <div><p className="text-xs text-gray-500 font-bold uppercase">Invoice Paid</p><p className="font-bold">INV-2048</p></div>
                            </div>
                            <div className="text-2xl font-black text-gray-900">₹4,250.00</div>
                        </div>

                        <div className="absolute -right-8 bottom-1/4 w-48 bg-gray-900 text-white p-4 rounded-2xl shadow-2xl border border-gray-800 z-20 hidden lg:block transform rotate-6 hover:rotate-0 transition-transform duration-300">
                            <p className="text-xs text-gray-400 font-bold uppercase mb-1">Today's Sales</p>
                            <div className="flex items-end gap-2">
                                <div className="text-3xl font-black">₹42k</div>
                                <TrendingUp className="w-5 h-5 text-green-400 mb-1" />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* 2. Trusted Statistics Section */}
            <section className="py-16 bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-x divide-gray-100">
                        {stats.map((stat, idx) => (
                            <motion.div 
                                key={idx} 
                                initial={{ opacity: 0, scale: 0.9 }} 
                                whileInView={{ opacity: 1, scale: 1 }} 
                                viewport={{ once: true }} 
                                transition={{ delay: idx * 0.1 }}
                                className="text-center px-4"
                            >
                                <div className="mx-auto w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center text-green-600 mb-4">
                                    <stat.icon size={24} />
                                </div>
                                <div className="text-3xl lg:text-4xl font-black text-gray-900 mb-1 tracking-tight">{stat.value}</div>
                                <div className="text-sm font-bold text-gray-500 uppercase tracking-widest">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 3. Billing Types Supported Section */}
            <section className="py-24 bg-gray-50 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-6 tracking-tight">Flexible Billing Modes</h2>
                        <p className="text-lg text-gray-600 font-medium">
                            Designed for every type of business workflow. Easily switch between billing modes with a fast and user-friendly interface.
                        </p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-12 items-center">
                        <div className="w-full lg:w-1/3 space-y-3">
                            {billingTypes.map((type, idx) => (
                                <div 
                                    key={idx}
                                    onClick={() => setActiveBillingType(idx)}
                                    className={`p-5 rounded-2xl cursor-pointer border-2 transition-all duration-300 ${activeBillingType === idx ? 'bg-white border-green-500 shadow-lg shadow-green-500/10' : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200'}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <h3 className={`font-bold text-lg ${activeBillingType === idx ? 'text-green-700' : 'text-gray-800'}`}>{type.name}</h3>
                                        {activeBillingType === idx && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                                    </div>
                                    <p className="text-sm text-gray-500 font-medium">{type.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="w-full lg:w-2/3">
                            <div className="bg-white p-6 md:p-10 rounded-[2rem] shadow-2xl border border-gray-100 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-green-100 to-transparent rounded-full blur-3xl opacity-50"></div>
                                
                                {/* Mockup UI */}
                                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
                                    <div className="bg-white p-4 border-b border-gray-200 flex justify-between items-center">
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center text-green-600 font-bold">INV</div>
                                            <span className="font-bold text-gray-800">New Order</span>
                                        </div>
                                        <div className="bg-gray-100 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 cursor-pointer hover:bg-gray-200 transition-colors border border-gray-200">
                                            {billingTypes[activeBillingType].name} <ChevronDown size={16} />
                                        </div>
                                    </div>
                                    <div className="p-8 text-center text-gray-400 font-medium min-h-[300px] flex flex-col items-center justify-center bg-dots-pattern">
                                        <ShoppingBag size={48} className="mb-4 text-gray-300" />
                                        <p>Cart is empty. Add products to generate {billingTypes[activeBillingType].name}.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. Features Section */}
            <section className="py-24 bg-white" id="features">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-green-600 font-bold uppercase tracking-widest text-sm mb-2 block">Powerful Features</span>
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight">Everything you need to grow</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, idx) => (
                            <div key={idx} className="group p-8 rounded-[2rem] bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-xl hover:shadow-green-900/5 hover:border-green-100 transition-all duration-300">
                                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-green-600 mb-6 shadow-sm border border-gray-100 group-hover:scale-110 transition-transform duration-300">
                                    <feature.icon size={28} />
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-500 font-medium leading-relaxed">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 5. How It Works Section */}
            <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight">How It Works</h2>
                        <p className="text-gray-400 mt-4 text-lg">Start billing in less than 3 minutes.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-gray-800 via-green-500 to-gray-800 z-0"></div>

                        {[
                            { step: '01', title: 'Create Business', desc: 'Sign up and add your business details & GSTIN.', icon: Settings },
                            { step: '02', title: 'Add Products', desc: 'Import items or add them manually with tax rates.', icon: Package },
                            { step: '03', title: 'Start Billing', desc: 'Generate professional invoices instantly.', icon: Printer }
                        ].map((item, idx) => (
                            <div key={idx} className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-24 h-24 rounded-[2rem] bg-gray-800 border-2 border-gray-700 flex items-center justify-center text-white mb-6 relative group hover:border-green-500 hover:bg-gray-900 transition-all">
                                    <item.icon size={40} className="group-hover:text-green-400 transition-colors" />
                                    <div className="absolute -top-3 -right-3 w-8 h-8 bg-green-500 text-white font-black text-sm flex items-center justify-center rounded-full border-4 border-gray-900">
                                        {item.step}
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                                <p className="text-gray-400 font-medium px-4">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 7. GST Compliance Section */}
            <section className="py-24 bg-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-16">
                        <div className="lg:w-1/2">
                            <span className="text-green-600 font-bold uppercase tracking-widest text-sm mb-2 block">100% Compliant</span>
                            <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight mb-6">GST Ready Billing</h2>
                            <p className="text-lg text-gray-600 font-medium mb-8">
                                Stay compliant with Indian tax laws effortlessly. Gio Billing automates all your GST calculations and reporting so you can focus on growing your business.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    'HSN/SAC Support', 'Multi GST Slabs', 'CGST / SGST / IGST Calculations',
                                    'Tax Reports', 'Professional Invoice Format', 'E-Way Bill Ready'
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
                                            <Check size={14} strokeWidth={3} />
                                        </div>
                                        <span className="font-bold text-gray-800">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lg:w-1/2 relative w-full">
                            <div className="bg-gradient-to-tr from-green-50 to-emerald-50 rounded-[3rem] p-8 md:p-12 border border-green-100">
                                <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                                    <div className="flex justify-between items-center border-b border-gray-100 pb-4 mb-4">
                                        <div className="font-black text-xl">TAX INVOICE</div>
                                        <div className="text-right">
                                            <div className="font-bold text-gray-900">GIO BILLING</div>
                                            <div className="text-xs text-gray-500">GSTIN: 29ABCDE1234F1Z5</div>
                                        </div>
                                    </div>
                                    <div className="space-y-3 mb-6">
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">Subtotal</span><span className="font-bold">₹1,000.00</span></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">CGST (9%)</span><span className="font-bold">₹90.00</span></div>
                                        <div className="flex justify-between text-sm"><span className="text-gray-500">SGST (9%)</span><span className="font-bold">₹90.00</span></div>
                                    </div>
                                    <div className="flex justify-between items-center border-t border-dashed border-gray-200 pt-4">
                                        <span className="font-bold text-gray-500 uppercase">Grand Total</span>
                                        <span className="font-black text-2xl text-gray-900">₹1,180.00</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 8. Dashboard Preview Section */}
            <section className="py-24 bg-gray-50 border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <span className="text-green-600 font-bold uppercase tracking-widest text-sm mb-2 block">Powerful Interface</span>
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight">Everything at your fingertips</h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Card: Sales Dashboard */}
                        <div className="bg-white rounded-[2rem] p-6 shadow-xl border border-gray-100 flex flex-col group">
                            <div className="mb-6">
                                <h3 className="text-2xl font-black text-gray-900 mb-2">Sales Dashboard</h3>
                                <p className="text-gray-500 font-medium">Real-time insights and analytics for your business growth.</p>
                            </div>
                            <div className="bg-gray-100 rounded-2xl overflow-hidden relative flex-1 min-h-[300px]">
                                <img src="/gio_billing_dashboard_mockup.png" alt="Sales Dashboard" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span class="text-gray-400 font-bold">Preview Image Loading...</span>'; }} />
                            </div>
                        </div>

                        {/* Right Card: GST Invoice Generation */}
                        <div className="bg-gray-900 rounded-[2rem] p-6 lg:p-10 shadow-2xl border border-gray-800 relative overflow-hidden group flex flex-col">
                            {/* Premium glowing background */}
                            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-green-500/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3 group-hover:bg-green-500/30 transition-colors duration-700"></div>
                            
                            <div className="relative z-10 mb-8">
                                <h3 className="text-2xl font-black text-white mb-2">GST Invoice Generation</h3>
                                <p className="text-gray-400 font-medium leading-relaxed">Create professional GST invoices instantly with automatic CGST, SGST & IGST calculations.</p>
                            </div>
                            
                            {/* Glowing badges */}
                            <div className="relative z-10 flex flex-wrap gap-2 mb-8">
                                {['GST Ready', 'Auto Tax Calculation', 'Instant Print', 'Cloud Backup'].map((badge, i) => (
                                    <span key={i} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-green-400 text-xs font-bold backdrop-blur-md">
                                        ✨ {badge}
                                    </span>
                                ))}
                            </div>

                            {/* Realistic Invoice Mockup built with Tailwind */}
                            <div className="relative z-10 mt-auto bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 transform group-hover:-translate-y-2 transition-transform duration-500">
                                {/* Top Bar */}
                                <div className="bg-green-50 px-5 py-4 border-b border-green-100 flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-green-500 rounded-lg text-white flex items-center justify-center font-bold text-sm shadow-md shadow-green-500/30">G</div>
                                        <span className="font-bold text-gray-900">GIO BILLING</span>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-0.5">Tax Invoice</div>
                                        <div className="text-sm font-bold text-gray-900">INV-2026-042</div>
                                    </div>
                                </div>
                                
                                <div className="p-5 bg-white">
                                    {/* Customer / Date */}
                                    <div className="flex justify-between items-end mb-5 border-b border-gray-100 pb-5">
                                        <div>
                                            <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Billed To</div>
                                            <div className="text-sm font-bold text-gray-900 mb-0.5">Acme Corporation Ltd.</div>
                                            <div className="text-xs text-gray-500 font-medium">GSTIN: 29ABCDE1234F1Z5</div>
                                        </div>
                                        <div className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div> Paid
                                        </div>
                                    </div>
                                    
                                    {/* Table Header */}
                                    <div className="grid grid-cols-12 gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-2">
                                        <div className="col-span-5">Product</div>
                                        <div className="col-span-2 text-center">Qty</div>
                                        <div className="col-span-2 text-right">Price</div>
                                        <div className="col-span-3 text-right">Total</div>
                                    </div>
                                    
                                    {/* Items */}
                                    <div className="space-y-1.5 mb-5">
                                        <div className="grid grid-cols-12 gap-2 text-sm items-center bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                            <div className="col-span-5 font-bold text-gray-900 text-xs">Premium Software License</div>
                                            <div className="col-span-2 text-center text-gray-600 text-xs font-medium">1</div>
                                            <div className="col-span-2 text-right text-gray-600 text-xs font-medium">₹1,000</div>
                                            <div className="col-span-3 text-right font-bold text-gray-900 text-xs">₹1,000</div>
                                        </div>
                                        <div className="grid grid-cols-12 gap-2 text-sm items-center p-2.5 rounded-lg border border-transparent">
                                            <div className="col-span-5 font-bold text-gray-900 text-xs">Setup & Training</div>
                                            <div className="col-span-2 text-center text-gray-600 text-xs font-medium">1</div>
                                            <div className="col-span-2 text-right text-gray-600 text-xs font-medium">₹500</div>
                                            <div className="col-span-3 text-right font-bold text-gray-900 text-xs">₹500</div>
                                        </div>
                                    </div>
                                    
                                    {/* Totals */}
                                    <div className="flex justify-between pt-4 border-t border-gray-100">
                                        {/* QR Code Placeholder */}
                                        <div className="w-16 h-16 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center opacity-70">
                                            <div className="grid grid-cols-3 gap-1">
                                                {[...Array(9)].map((_, i) => <div key={i} className={`w-1 h-1 bg-gray-400 rounded-sm ${i % 2 === 0 ? 'opacity-100' : 'opacity-40'}`}></div>)}
                                            </div>
                                        </div>
                                        
                                        <div className="w-1/2 space-y-2.5">
                                            <div className="flex justify-between text-xs text-gray-500 font-medium">
                                                <span>Subtotal</span><span>₹1,500.00</span>
                                            </div>
                                            <div className="flex justify-between text-xs text-gray-500 font-medium">
                                                <span>CGST (9%)</span><span>₹135.00</span>
                                            </div>
                                            <div className="flex justify-between text-xs text-gray-500 font-medium">
                                                <span>SGST (9%)</span><span>₹135.00</span>
                                            </div>
                                            <div className="flex justify-between items-center border-t border-dashed border-gray-200 pt-3 mt-1">
                                                <span className="text-xs font-black text-gray-500 uppercase tracking-widest">Grand Total</span>
                                                <span className="text-xl font-black text-green-600">₹1,770.00</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 9. Testimonials Section */}
            <section className="py-24 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tight">Loved by Merchants</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { name: 'Rajesh Kumar', role: 'Supermarket Owner', text: 'Gio Billing completely transformed how we handle our checkout lines. It is incredibly fast and the GST reports save us hours every month.' },
                            { name: 'Priya Sharma', role: 'Retail Store Manager', text: 'The inventory management feature is a lifesaver. We know exactly when to restock and the barcode scanner integration works flawlessly.' },
                            { name: 'Amit Patel', role: 'Wholesale Distributor', text: 'Managing credit bills and ledgers used to be a nightmare. This software makes it so easy to track who owes what, and the mobile app is perfect.' }
                        ].map((testimonial, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-[2rem] p-8 border border-gray-100 relative">
                                <div className="flex gap-1 mb-4 text-yellow-400">
                                    {[1, 2, 3, 4, 5].map(i => <Star key={i} size={18} fill="currentColor" />)}
                                </div>
                                <p className="text-gray-600 font-medium leading-relaxed mb-6">"{testimonial.text}"</p>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold text-lg">
                                        {testimonial.name[0]}
                                    </div>
                                    <div>
                                        <div className="font-bold text-gray-900">{testimonial.name}</div>
                                        <div className="text-sm text-gray-500">{testimonial.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* 11. FAQ Section */}
            <section className="py-24 bg-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-black text-gray-900 tracking-tight">Frequently Asked Questions</h2>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-2xl overflow-hidden bg-gray-50">
                                <button 
                                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                                    className="w-full px-6 py-4 text-left flex justify-between items-center bg-white font-bold text-gray-900 hover:bg-gray-50"
                                >
                                    {faq.q}
                                    <ChevronDown className={`w-5 h-5 transition-transform ${activeFaq === idx ? 'rotate-180' : ''}`} />
                                </button>
                                {activeFaq === idx && (
                                    <div className="px-6 py-4 text-gray-600 font-medium border-t border-gray-100 bg-gray-50">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 12. Final CTA Section */}
            <section className="py-24 bg-gray-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-green-900/20 via-gray-900 to-gray-900 opacity-80"></div>
                <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
                    <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight">Start Managing Your Business Smarter</h2>
                    <p className="text-xl text-gray-400 font-medium mb-10 max-w-2xl mx-auto">
                        Powerful billing and inventory software designed for modern businesses. Join thousands of merchants today.
                    </p>
                    <button onClick={openDemoModal} className="px-10 py-5 rounded-2xl bg-green-600 text-white font-black text-xl hover:bg-green-500 shadow-2xl shadow-green-500/20 transition-all hover:scale-105 active:scale-95">
                        Get Started Today
                    </button>
                </div>
            </section>
        </div>
    );
};

export default HomePage;
